import { router } from "expo-router";

import { billingApi } from "@/api";
import { ApiError } from "@/api/client";
import i18n from "@/i18n";

import type {
  PackPrice,
  PaymentProvider,
  PlanId,
  PlanPrices,
  PurchaseResult,
} from "./types";

/**
 * Web provider: asks the backend to create a Stripe Checkout / Billing Portal
 * session. Plus and the portal redirect the browser to Stripe's hosted page;
 * on return Stripe sends the user back to the profile route (see
 * STRIPE_SUCCESS_URL) and the subscription is activated server-side by the
 * Stripe webhook.
 *
 * Practice packs are the exception: their session is created in EMBEDDED mode
 * and rendered inside our own /pack-checkout screen, so buying replies never
 * leaves the app. See `purchasePack`.
 */
export const stripeProvider: PaymentProvider = {
  name: "stripe",

  /**
   * Null on purpose: on web our own numbers ARE the charge. The amounts in
   * constants/plans.ts are the amounts in Stripe, and the currency is resolved
   * server-side from the caller's location, so there is no third party whose
   * price could disagree. Native is the case that needed asking (see
   * PlanPrices).
   */
  async prices(): Promise<PlanPrices | null> {
    return null;
  },

  async purchase(plan: PlanId): Promise<PurchaseResult> {
    // opncue Plus prices are multi-currency; the server picks which currency
    // to charge from the caller's location (£ UK / € Europe / $ elsewhere) —
    // the same resolution the displayed price used.
    return redirectTo(() => billingApi.checkout(plan));
  },

  /**
   * Nothing to restore on the web rail. A Stripe subscription is attached to
   * the account, not to a device or a store login, so it is already present
   * the moment the user signs in — there is no receipt that can be stranded.
   * The paywall does not offer the control here.
   */
  async restore(): Promise<PurchaseResult> {
    return { status: "unavailable" };
  },

  async manage(): Promise<PurchaseResult> {
    return redirectTo(() => billingApi.portal());
  },

  /**
   * Null for the same reason `prices()` is: on web our own numbers ARE the
   * charge, so the caller uses the `price` GET /practice/packs already sent.
   */
  async packPrices(): Promise<PackPrice[] | null> {
    return null;
  },

  async purchasePack(pack: { id: string }): Promise<PurchaseResult> {
    // One-time Checkout Session, priced and validated server-side; the pack is
    // credited by the Stripe webhook (and by /billing/confirm on return),
    // never by anything the browser reports back.
    //
    // Unlike Plus, this does NOT leave the site. The session is created in
    // EMBEDDED mode and mounted inside our own /pack-checkout screen, which
    // owns creating it — Stripe needs the session to exist before the form can
    // render, so the screen asks for one when it mounts rather than this
    // provider making one the buyer might never see.
    //
    // `router.push` is the in-app counterpart of the `window.location.assign`
    // the hosted flow used: same kind of imperative navigation, one that keeps
    // the buyer on our page.
    if (typeof window === "undefined") {
      return { status: "unavailable" };
    }
    router.push({
      pathname: "/pack-checkout",
      params: { pack: pack.id },
    });
    // The screen takes it from here; the outcome is confirmed on return via
    // the Stripe webhook + POST /billing/confirm, exactly as before.
    return { status: "pending" };
  },

  async trialInfo(trigger?: string): Promise<{ days: number } | null> {
    try {
      const offer = await billingApi.trial(trigger);
      return offer.eligible && offer.days > 0 ? { days: offer.days } : null;
    } catch {
      // No answer = no offer shown; the server re-decides at subscribe anyway.
      return null;
    }
  },
};

async function redirectTo(
  create: () => Promise<{ url: string }>,
): Promise<PurchaseResult> {
  if (typeof window === "undefined") {
    return { status: "unavailable" };
  }

  try {
    const { url } = await create();
    if (!url) {
      return {
        status: "failed",
        message: i18n.t("billing.provider.paymentFailed"),
      };
    }
    window.location.assign(url);
    // The browser is navigating away; the outcome is confirmed on return via
    // the Stripe webhook + a fresh /users/me.
    return { status: "pending" };
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) {
      return {
        status: "failed",
        message: i18n.t("billing.provider.paymentFailed"),
      };
    }
    return {
      status: "failed",
      message: i18n.t("billing.provider.paymentFailed"),
    };
  }
}
